/** Your recipes as a list: the filter the Recipes page and the Log sheet share, the newest few, and a serving to log. */
import type { RecipeSummary } from '../../shared/api';
import { browserTimeZone, todayIn, type ISODate } from '../../shared/dates';
import { buildIndex, searchIndex } from '../../shared/search';
import type { LogItem } from '../components/log/logItem';

/**
 * The recipes that match what's typed, in the list's own order (newest first). The word search
 * only knows Latin letters, so a name that simply contains what's typed (水饺, борщ) matches too.
 */
export function matchRecipes(list: readonly RecipeSummary[], query: string): RecipeSummary[] {
  const q = query.trim();
  if (!q) return [...list];
  const index = buildIndex(list.map((r) => ({ ref: r.id, name: r.name })));
  const hits = new Set(searchIndex(index, q, { limit: list.length }).map((h) => h.ref));
  const typed = q.toLocaleLowerCase();
  return list.filter((r) => hits.has(r.id) || r.name.toLocaleLowerCase().includes(typed));
}

/** When a recipe was made, in ms. One without a readable date counts as the oldest. */
function madeAt(recipe: Pick<RecipeSummary, 'createdAt'>): number {
  const ms = Date.parse(recipe.createdAt);
  return Number.isFinite(ms) ? ms : -Infinity;
}

/**
 * Your `count` newest recipes, newest first. Ones made at the same moment keep the list's order,
 * and so do ones without a readable date (two of those compare as NaN, which sort treats as equal).
 */
export function newestRecipes(list: readonly RecipeSummary[], count: number): RecipeSummary[] {
  return [...list].sort((a, b) => madeAt(b) - madeAt(a)).slice(0, count);
}

/** Any moment of a local date is within 26 hours of noon UTC that day (time zones run from UTC−12 to UTC+14). */
const NEAR_DAY_MS = 2 * 86_400_000;

/**
 * Whether a recipe was made on `today`, the person's date, going by the clock in `timeZone`
 * (the browser's when left out). Anything nowhere near that day skips the time zone work,
 * so a long list stays cheap.
 */
export function madeToday(recipe: Pick<RecipeSummary, 'createdAt'>, today: ISODate, timeZone?: string): boolean {
  const ms = madeAt(recipe);
  if (!Number.isFinite(ms) || Math.abs(ms - Date.parse(`${today}T12:00:00Z`)) > NEAR_DAY_MS) return false;
  return todayIn(timeZone ?? browserTimeZone(), new Date(ms)) === today;
}

/** Some of a recipe to log: one serving unless `grams` says otherwise. */
export function recipeLogItem(recipe: RecipeSummary, grams = recipe.food.servings[0]?.grams ?? 100): LogItem {
  return {
    name: recipe.name,
    brand: null,
    foodRef: recipe.food.ref,
    grams,
    per100: recipe.food.per100,
    servings: recipe.food.servings,
    source: 'recipe',
    confidence: null,
  };
}
